<template>
  <div class="relative" ref="selectRef">
    <!-- 触发器 -->
    <div
      @click="toggleDropdown"
      class="relative w-full h-10 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors flex items-center"
      :class="{ 'border-blue-500 ring-1 ring-blue-500': isOpen }"
    >
      <span 
        v-if="selectedOption" 
        class="flex-1 truncate text-sm text-gray-900"
      >
        {{ selectedOption[labelKey] }}
      </span>
      <span 
        v-else 
        class="flex-1 truncate text-sm text-gray-500"
      >
        {{ placeholder }}
      </span>
      
      <!-- 箭头图标 -->
      <span class="pointer-events-none flex items-center pl-2">
        <svg 
          class="h-5 w-5 text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
        </svg>
      </span>
    </div>

    <!-- 下拉列表 -->
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-show="isOpen"
        class="absolute z-[60] mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
      >
        <!-- 搜索框 -->
        <div class="sticky top-0 z-[70] bg-white px-3 py-2 border-b border-gray-100">
          <div class="relative">
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              placeholder="搜索..."
              class="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
              @keydown.esc="closeDropdown"
              @keydown.enter.prevent="selectFirstOption"
              @keydown.arrow-down.prevent="navigateDown"
              @keydown.arrow-up.prevent="navigateUp"
            />
            <svg class="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <!-- 选项列表 -->
        <div class="py-1">
          <!-- 空选项 -->
          <div
            v-if="clearable"
            @click="clearOption"
            class="group relative cursor-pointer select-none py-1.5 pl-3 pr-9 text-gray-400 hover:bg-blue-50 hover:text-blue-900 transition-colors text-sm"
            :class="{ 'bg-blue-100': !modelValue }"
          >{{ placeholder }}</div>
          <div
            v-for="(option, index) in filteredOptions"
            :key="option[valueKey]"
            @click="selectOption(option)"
            class="group relative cursor-pointer select-none py-1.5 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
            :class="{ 
              'bg-blue-50 text-blue-900': highlightedIndex === index,
              'bg-blue-100 text-blue-900': selectedOption && selectedOption[valueKey] === option[valueKey]
            }"
          >
            <span class="block truncate text-sm font-medium">
              {{ option[labelKey] }}
            </span>
            
            <!-- 选中图标 -->
            <span 
              v-if="selectedOption && selectedOption[valueKey] === option[valueKey]"
              class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
            >
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </span>
          </div>

          <!-- 无结果状态 -->
          <div 
            v-if="filteredOptions.length === 0" 
            class="relative cursor-default select-none py-4 pl-3 pr-9 text-gray-500"
          >
            <span class="block truncate text-sm">
              {{ searchQuery ? '没有找到匹配选项' : '暂无选项' }}
            </span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'

// Props
const props = defineProps({
  modelValue: {
    type: [String, Number, Object],
    default: null
  },
  options: {
    type: Array,
    required: true,
    default: () => []
  },
  placeholder: {
    type: String,
    default: '请选择...'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'change'])

// Refs
const selectRef = ref(null)
const searchInputRef = ref(null)

// State
const isOpen = ref(false)
const searchQuery = ref('')
const highlightedIndex = ref(-1)

// Computed
const selectedOption = computed(() => {
  if (!props.modelValue) return null
  return props.options.find(option => option[props.valueKey] === props.modelValue)
})

const filteredOptions = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.options
  }
  
  const query = searchQuery.value.toLowerCase()
  return props.options.filter(option => 
    option[props.labelKey].toLowerCase().includes(query)
  )
})

// Methods
const toggleDropdown = () => {
  if (props.disabled) return
  
  if (isOpen.value) {
    closeDropdown()
  } else {
    openDropdown()
  }
}

const openDropdown = async () => {
  isOpen.value = true
  highlightedIndex.value = -1
  
  await nextTick()
  if (searchInputRef.value) {
    searchInputRef.value.focus()
  }
}

const closeDropdown = () => {
  isOpen.value = false
  searchQuery.value = ''
  highlightedIndex.value = -1
}

const selectOption = (option) => {
  emit('update:modelValue', option[props.valueKey])
  emit('change', option)
  closeDropdown()
}

const clearOption = () => {
  emit('update:modelValue', null)
  emit('change', null)
  closeDropdown()
}

const selectFirstOption = () => {
  if (filteredOptions.value.length > 0) {
    selectOption(filteredOptions.value[0])
  }
}

const navigateDown = () => {
  if (highlightedIndex.value < filteredOptions.value.length - 1) {
    highlightedIndex.value++
  }
}

const navigateUp = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
  }
}

// 点击外部关闭
const handleClickOutside = (event) => {
  if (selectRef.value && !selectRef.value.contains(event.target)) {
    closeDropdown()
  }
}

// 监听器
watch(isOpen, (newValue) => {
  if (newValue) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

watch(searchQuery, () => {
  highlightedIndex.value = -1
})

// 生命周期
onMounted(() => {
  // 初始化时不需要添加监听器，只在打开时添加
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* 确保下拉框在正确的层级显示 */
.z-10 {
  z-index: 10;
}
</style>
